<template>
  <header class="prdh" :class="`prdh--${pageTone}`">
    <ExBreadcrumbs
      class="prdh__breadcrumbs"
      :breadcrumbs-list="breadcrumbs"
      @onClickBreadcrumb="onBreadcrumbClick"
    />

    <div class="prdh__cards" :class="{ 'prdh__cards--merged': mergedBanner }">
      <section class="prdh__identity" aria-label="Pay range identity">
        <p class="prdh__identity-text">
          <span
            v-for="(segment, index) in identitySegments"
            :key="`${index}-${segment}`"
            class="prdh__segment"
          >
            <span v-if="index > 0" class="prdh__sep" aria-hidden="true">•</span>
            {{ segment }}
          </span>
        </p>

        <DropDownMenu
          v-if="menuItems.length"
          class="prdh__menu"
          :dropdown-items="menuItems"
          :is-open="false"
          :width="180"
          tip-side="right"
          :translate-y-offset="8"
          @onClick="onMenuAction"
        >
          <template #activator>
            <button type="button" class="prdh__menu-btn" aria-label="Pay range actions">
              <img :src="dotsIcon" alt="" width="4" height="18" />
            </button>
          </template>
        </DropDownMenu>
      </section>

      <hr v-if="mergedBanner" class="prdh__divider" aria-hidden="true" />

      <section
        v-if="displayedVersion"
        class="prdh__version"
        :class="`prdh__version--${versionTone}`"
        aria-label="Pay range version"
      >
        <div class="prdh__version-row">
          <div class="prdh__badges">
            <PayPlanLifecycleBadge :status="statusBadge.tone" :label="statusBadge.label" />
            <OrgObjectStatusBadge v-if="archivedTag" neutral :label="archivedTag.label" />
            <PayPlanPeriodStateBadge v-if="periodState" :state="periodState" :filled="isDraft" />
            <VersionBadge
              v-if="displayedVersion.versionCode"
              :code="displayedVersion.versionCode"
            />
          </div>
          <span v-if="amountLabel" class="prdh__amount">{{ amountLabel }}</span>
          <span v-if="periodText" class="prdh__period">{{ periodText }}</span>

          <div class="prdh__actions">
            <button type="button" class="prdh__open" @click="emit('open')">Open</button>
            <DropDownMenu
              v-if="!mergedBanner && versionMenuItems.length"
              class="prdh__menu"
              :dropdown-items="versionMenuItems"
              :is-open="false"
              :width="180"
              tip-side="right"
              :translate-y-offset="8"
              @onClick="onVersionMenuAction"
            >
              <template #activator>
                <button type="button" class="prdh__menu-btn" aria-label="Version actions">
                  <img :src="dotsIcon" alt="" width="4" height="18" />
                </button>
              </template>
            </DropDownMenu>
          </div>
        </div>

        <div v-if="readiness.length" class="prdh__readiness">
          <span class="prdh__readiness-title">@ Readiness</span>
          <ul class="prdh__readiness-list">
            <li v-for="(point, index) in readiness" :key="point.label" class="prdh__readiness-item">
              <span class="prdh__readiness-label">{{ point.label }}</span>
              <span class="prdh__readiness-dash" aria-hidden="true">-</span>
              <span class="prdh__readiness-end">
                <span class="prdh__readiness-value">{{ point.value }}</span>
                <span
                  v-if="index < readiness.length - 1"
                  class="prdh__readiness-comma"
                  aria-hidden="true"
                  >,</span
                >
              </span>
            </li>
          </ul>
        </div>
      </section>
    </div>

    <div v-if="banner" class="prdh__banner" role="status">
      <svg
        class="prdh__banner-icon"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path
          d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
        />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <p class="prdh__banner-text">{{ banner }}</p>
    </div>
  </header>
</template>

<script setup>
import { computed } from "vue";

import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import DropDownMenu from "@components/DropDownMenu/DropDownMenu.vue";
import PayPlanLifecycleBadge from "@components/PayPlanLifecycleBadge/PayPlanLifecycleBadge.vue";
import PayPlanPeriodStateBadge from "@components/PayPlanPeriodStateBadge/PayPlanPeriodStateBadge.vue";
import VersionBadge from "@components/VersionBadge/VersionBadge.vue";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import { versionStatusBadge } from "@components/FairPay/payRangeBadges";
import dotsIcon from "@components/PayRangeVersionRow/icon-dots-vertical.svg";

const props = defineProps({
  /** Tint of the whole page: default | retired | archived */
  pageTone: {
    type: String,
    default: "default",
    validator: (value) => ["default", "retired", "archived"].includes(value),
  },
  /** Tint of the version banner, from the displayed version: current | future | lapsed | draft */
  versionTone: {
    type: String,
    default: "current",
    validator: (value) => ["current", "future", "lapsed", "draft"].includes(value),
  },
  /** Identity banner segments, joined by a • separator: code, job family › level, currency … */
  identitySegments: { type: Array, default: () => [] },
  /**
   * The version the banner describes: `{ versionCode, status: draft | in-force | retired | voided,
   * periodState, firstDraft }`. No version → identity banner only. A first draft
   * (`firstDraft`) merges both banners into one card and has an empty `versionCode`.
   */
  displayedVersion: { type: Object, default: null },
  /** `{ label: "Archived" }` — neutral tag after the status badge when the range is archived. */
  archivedTag: { type: Object, default: null },
  /** "USD 40K – 50K" */
  amountLabel: { type: String, default: "" },
  /** "Jul 1, 2025 – Open-ended" */
  periodText: { type: String, default: "" },
  /** `[{ label: "0%", value: "40K" }, …]` */
  readiness: { type: Array, default: () => [] },
  /** Retired / archived strip under the banners. */
  banner: { type: String, default: "" },
  /** Pay range lifecycle actions for the identity banner ⋯ menu. */
  menuItems: { type: Array, default: () => [] },
  /** Displayed-version actions for the version banner ⋯ menu (not shown on a merged first draft). */
  versionMenuItems: { type: Array, default: () => [] },
});

const emit = defineEmits(["navigate-list", "open", "menu-action", "version-menu-action"]);

const breadcrumbs = [
  { text: "Pay Ranges", disabled: false },
  { text: "Pay Range", disabled: true },
];

const isDraft = computed(() => props.displayedVersion?.status === "draft");

const mergedBanner = computed(() => isDraft.value && Boolean(props.displayedVersion?.firstDraft));

const statusBadge = computed(
  () => versionStatusBadge(props.displayedVersion) || versionStatusBadge("in-force")
);

const periodState = computed(() => props.displayedVersion?.periodState || "");

function onBreadcrumbClick(crumb) {
  if (crumb?.text === "Pay Ranges") emit("navigate-list");
}

function onMenuAction(item) {
  emit("menu-action", item?.value ?? item);
}

function onVersionMenuAction(item) {
  emit("version-menu-action", item?.value ?? item);
}
</script>

<style scoped>
.prdh {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  font-family: Inter, sans-serif;
}

.prdh--retired {
  background: #f2f4f7;
}

.prdh--archived {
  background: #fef3f2;
}

.prdh__cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.prdh__cards--merged {
  box-sizing: border-box;
  gap: 8px;
  padding: 0;
  border: 1.5px dashed var(--component-colors-utility-fuchsia-utility-fuchsia-500, #d444f1);
  border-radius: 8px;
  background: var(--component-colors-utility-fuchsia-utility-fuchsia-50, #fdf4ff);
}

.prdh__divider {
  width: calc(100% - 17px);
  height: 1px;
  margin: 0 4px 0 13px;
  padding: 0;
  border: 0;
  background: var(--component-colors-utility-fuchsia-utility-fuchsia-500, #d444f1);
}

.prdh__cards--merged .prdh__identity,
.prdh__cards--merged .prdh__version {
  border: 0;
  border-radius: 0;
  background: transparent;
}

.prdh__identity {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 0 4px 0 13px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #ffffff;
}

.prdh__identity-text {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-width: 0;
  margin: 0;
  padding: 8px 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.prdh__segment {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.prdh__version {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 6px 4px 6px 13px;
  border: 1px solid #75e0a7;
  border-radius: 8px;
  background: #dcfae6;
  color: #101828;
  --prdh-label: #475467;
  --prdh-value: #344054;
}

.prdh__version--future {
  border-color: #b2ddff;
  background: #eff8ff;
  --prdh-label: #667085;
}

.prdh__version--lapsed {
  border-color: #d0d5dd;
  background: #f2f4f7;
  --prdh-label: #667085;
}

.prdh__version--draft {
  border: 1.5px dashed var(--component-colors-utility-fuchsia-utility-fuchsia-500, #d444f1);
  background: var(--component-colors-utility-fuchsia-utility-fuchsia-50, #fdf4ff);
  --prdh-label: var(--colors-text-text-primary-900, #101828);
  --prdh-value: var(--colors-text-text-primary-900, #101828);
}

.prdh__cards--merged .prdh__version--draft {
  border: 0;
  background: transparent;
}

.prdh__cards--merged .prdh__identity-text,
.prdh__version--draft .prdh__period {
  color: var(--colors-text-text-primary-900, #101828);
}

.prdh__version-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.prdh__badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.prdh__amount {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: var(--prdh-value);
}

.prdh__period {
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #101828;
}

.prdh__actions {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.prdh__open {
  padding: 8px 12px;
  border: 1px solid #7f56d9;
  border-radius: 8px;
  background: #7f56d9;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #ffffff;
  cursor: pointer;
}

.prdh__open:hover {
  border-color: #6941c6;
  background: #6941c6;
}

.prdh__open:focus-visible,
.prdh__menu-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.prdh__menu-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.prdh__menu-btn:hover {
  background: #f2edff;
}

.prdh__readiness {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.prdh__readiness-title {
  color: var(--prdh-label);
}

.prdh__readiness-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.prdh__readiness-item {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
}

.prdh__readiness-label,
.prdh__readiness-dash,
.prdh__readiness-comma {
  color: var(--prdh-label);
}

.prdh__readiness-end {
  display: inline-flex;
  align-items: baseline;
}

.prdh__readiness-value {
  font-weight: 600;
  color: var(--prdh-value);
}

.prdh__banner {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 32px;
  border-top: 1px solid #d0d5dd;
  border-bottom: 1px solid #d0d5dd;
  background: #ffffff;
}

.prdh__banner-icon {
  flex-shrink: 0;
  color: #b54708;
}

.prdh__banner-text {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}
</style>
